<template>
    <div class="container-fluid pa-2 pa-md-3">
        <!-- Header -->
        <v-layout row wrap align-center class="mb-3">
            <v-flex xs12>
                <div class="d-flex align-center justify-space-between flex-wrap">
                    <div class="mb-2 mb-md-0">
                        <h2 class="primary--text title-mobile">
                            <v-icon color="primary">mdi-map-marker</v-icon>
                            Shipping Points
                        </h2>
                        <p class="grey--text caption mb-0 hidden-xs-only">Manage delivery points and GPS coordinates</p>
                    </div>
                    <div>
                        <v-btn color="primary" @click="openAddDialog" small class="mb-1">
                            <v-icon small left>mdi-plus</v-icon>
                            <span class="hidden-xs-only">New Point</span>
                            <span class="hidden-sm-and-up">Add</span>
                        </v-btn>

                        <v-tooltip bottom v-if="pointsWithoutCoords > 0">
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    icon
                                    small
                                    color="warning"
                                    @click="autoFillDialog = true"
                                    v-on="on"
                                    class="ml-1 mb-1"
                                >
                                    <v-icon small>mdi-crosshairs-gps</v-icon>
                                </v-btn>
                            </template>
                            <span>Auto-fill GPS</span>
                        </v-tooltip>
                    </div>
                </div>
            </v-flex>
        </v-layout>

        <!-- Dialog Auto-Fill Confirmation -->
        <v-dialog v-model="autoFillDialog" max-width="500px">
            <v-card>
                <v-card-title class="warning white--text pa-3">
                    <v-icon left color="white">mdi-alert</v-icon>
                    Auto-Fill GPS Coordinates
                </v-card-title>
                <v-card-text class="pt-4">
                    <p>
                        <strong>{{ pointsWithoutCoords }}</strong> shipping point(s) don't have GPS coordinates.
                    </p>
                    <p>
                        This action will automatically assign coordinates based on their town's location.
                    </p>
                    <v-alert type="info" :value="true" class="mt-3">
                        <v-icon left>mdi-information</v-icon>
                        <strong>Note:</strong> Only points in known towns will be updated.
                        You can manually adjust coordinates later if needed.
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-btn flat @click="autoFillDialog = false">Cancel</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="warning"
                        @click="autoFillCoordinates"
                        :loading="autoFilling"
                    >
                        <v-icon left>mdi-crosshairs-gps</v-icon>
                        Auto-Fill Now
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Statistics -->
        <v-layout row wrap class="mb-3">
            <v-flex xs6 sm6 md3 class="pa-1">
                <v-card color="primary" dark class="stat-card">
                    <v-card-text class="pa-2 text-xs-center">
                        <div class="caption">Total</div>
                        <div class="headline">{{ shippingPoints.length }}</div>
                    </v-card-text>
                </v-card>
            </v-flex>
            <v-flex xs6 sm6 md3 class="pa-1">
                <v-card color="success" dark class="stat-card">
                    <v-card-text class="pa-2 text-xs-center">
                        <div class="caption">With GPS</div>
                        <div class="headline">{{ pointsWithCoords }}</div>
                    </v-card-text>
                </v-card>
            </v-flex>
            <v-flex xs6 sm6 md3 class="pa-1">
                <v-card color="warning" dark class="stat-card">
                    <v-card-text class="pa-2 text-xs-center">
                        <div class="caption">No GPS</div>
                        <div class="headline">{{ pointsWithoutCoords }}</div>
                    </v-card-text>
                </v-card>
            </v-flex>
            <v-flex xs6 sm6 md3 class="pa-1">
                <v-card color="info" dark class="stat-card">
                    <v-card-text class="pa-2 text-xs-center">
                        <div class="caption">Towns</div>
                        <div class="headline">{{ towns.length }}</div>
                    </v-card-text>
                </v-card>
            </v-flex>
        </v-layout>

        <!-- Location Information Display -->
        <v-card class="mb-3">
            <v-card-title class="teal darken-2 white--text pa-2">
                <v-icon small color="white" class="mr-2">mdi-map</v-icon>
                <span class="body-2">Location Overview</span>
            </v-card-title>
            <v-card-text class="pa-3">
                <div class="location-overview">
                    <v-layout row wrap>
                        <v-flex xs12 md6 class="pa-2">
                            <h4 class="primary--text mb-3">Points with GPS Coordinates</h4>
                            <div v-for="point in shippingPointsWithCoords" :key="point.id" class="location-item mb-2">
                                <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                                <strong>{{ point.name }}</strong> - {{ point.town_name }}
                                <div class="coordinates ml-4">
                                    <small class="grey--text">Lat: {{ point.latitude }}, Lng: {{ point.longitude }}</small>
                                </div>
                            </div>
                            <v-alert v-if="shippingPointsWithCoords.length === 0" type="info" :value="true" class="mt-2">
                                No points with GPS coordinates found.
                            </v-alert>
                        </v-flex>
                        <v-flex xs12 md6 class="pa-2">
                            <h4 class="warning--text mb-3">Points Needing GPS</h4>
                            <div v-for="point in shippingPointsWithoutCoords" :key="point.id" class="location-item mb-2">
                                <v-icon small color="warning" class="mr-2">mdi-alert</v-icon>
                                <strong>{{ point.name }}</strong> - {{ point.town_name }}
                                <v-btn x-small color="primary" @click="editPoint(point)" class="ml-2">
                                    Add Coordinates
                                </v-btn>
                            </div>
                            <v-alert v-if="shippingPointsWithoutCoords.length === 0" type="success" :value="true" class="mt-2">
                                All points have GPS coordinates!
                            </v-alert>
                        </v-flex>
                    </v-layout>
                </div>
            </v-card-text>
        </v-card>

        <!-- Filters -->
        <v-card class="mb-3">
            <v-card-text class="pa-2">
                <v-layout row wrap>
                    <v-flex xs12 sm4 class="pa-1">
                        <v-select
                            v-model="filters.town"
                            :items="townOptions"
                            label="Town"
                            clearable
                            prepend-icon="mdi-city"
                            hide-details
                        ></v-select>
                    </v-flex>
                    <v-flex xs12 sm4 class="pa-1">
                        <v-select
                            v-model="filters.hasCoords"
                            :items="coordsOptions"
                            label="GPS Status"
                            clearable
                            prepend-icon="mdi-gps-fixed"
                            hide-details
                        ></v-select>
                    </v-flex>
                    <v-flex xs12 sm4 class="pa-1">
                        <v-text-field
                            v-model="filters.search"
                            label="Search"
                            prepend-icon="mdi-magnify"
                            clearable
                            hide-details
                        ></v-text-field>
                    </v-flex>
                </v-layout>
            </v-card-text>
        </v-card>

        <!-- Points List -->
        <v-layout row wrap>
            <v-flex
                v-for="point in filteredPoints"
                :key="point.id"
                xs12 sm6 md4 lg3
                class="pa-1 pa-md-2"
            >
                <v-card
                    class="point-card"
                    :class="{ 'no-coords': !point.has_coordinates }"
                >
                    <v-card-text class="pa-2">
                        <v-layout row wrap align-center class="mb-2">
                            <v-flex xs8>
                                <h3 class="font-weight-bold body-2">{{ point.name }}</h3>
                            </v-flex>
                            <v-flex xs4 text-xs-right>
                                <v-chip
                                    small
                                    :color="point.has_coordinates ? 'success' : 'warning'"
                                    text-color="white"
                                >
                                    <v-icon x-small left>
                                        {{ point.has_coordinates ? 'mdi-check-circle' : 'mdi-alert' }}
                                    </v-icon>
                                    <span class="caption">{{ point.has_coordinates ? 'GPS' : 'No' }}</span>
                                </v-chip>
                            </v-flex>
                        </v-layout>

                        <div class="info-section">
                            <div class="mb-1">
                                <v-icon small color="teal">mdi-city</v-icon>
                                <span class="caption">
                                    <strong>Town:</strong>
                                    <v-chip x-small color="info" text-color="white" class="ml-1">
                                        {{ point.town_name }}
                                    </v-chip>
                                </span>
                            </div>
                            <div class="mb-1">
                                <v-icon small color="green">mdi-currency-usd</v-icon>
                                <span class="caption"><strong>Fee:</strong> {{ point.fee }} FCFA</span>
                            </div>
                            <div class="mb-1">
                                <v-icon small color="orange">mdi-clock</v-icon>
                                <span class="caption"><strong>Delivery:</strong> {{ point.time_interval_days }} days</span>
                            </div>

                            <div v-if="point.has_coordinates" class="mt-2 success--text">
                                <v-icon small color="success">mdi-gps-fixed</v-icon>
                                <span class="caption">
                                    <strong>Coordinates:</strong><br>
                                    <small>Lat: {{ point.latitude }}<br>Lng: {{ point.longitude }}</small>
                                </span>
                            </div>
                        </div>
                    </v-card-text>

                    <v-card-actions class="pa-2">
                        <v-btn
                            small
                            color="primary"
                            @click="editPoint(point)"
                            flat
                        >
                            <v-icon small left>mdi-pencil</v-icon>
                            <span class="hidden-xs-only">Edit</span>
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn
                            v-if="point.has_coordinates"
                            small
                            color="success"
                            @click="showLocationInfo(point)"
                            icon
                        >
                            <v-icon small>mdi-information</v-icon>
                        </v-btn>
                        <v-btn
                            v-else
                            small
                            color="warning"
                            @click="addCoordinates(point)"
                            icon
                        >
                            <v-icon small>mdi-map-marker-plus</v-icon>
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-flex>

            <v-flex v-if="filteredPoints.length === 0" xs12>
                <v-alert type="info" :value="true">
                    <v-icon left>mdi-information</v-icon>
                    No shipping points found. Create a new one!
                </v-alert>
            </v-flex>
        </v-layout>

        <!-- Add/Edit Dialog -->
        <v-dialog v-model="dialog" max-width="600px" persistent scrollable>
            <v-card>
                <v-card-title class="primary white--text pa-3">
                    <v-icon left color="white">mdi-map-marker</v-icon>
                    <span class="headline">{{ isEditing ? 'Edit' : 'New' }} Shipping Point</span>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="closeDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pt-4">
                    <v-form ref="form" v-model="valid">
                        <v-layout row wrap>
                            <v-flex xs12 sm6 class="pa-2">
                                <v-text-field
                                    v-model="formData.name"
                                    label="Point Name *"
                                    :rules="[v => !!v || 'Name is required']"
                                    prepend-icon="mdi-label"
                                    required
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12 sm6 class="pa-2">
                                <v-select
                                    v-model="formData.town"
                                    :items="towns"
                                    item-text="name"
                                    item-value="id"
                                    label="Town *"
                                    :rules="[v => !!v || 'Town is required']"
                                    prepend-icon="mdi-city"
                                    required
                                ></v-select>
                            </v-flex>
                        </v-layout>

                        <v-layout row wrap>
                            <v-flex xs12 sm6 class="pa-2">
                                <v-text-field
                                    v-model.number="formData.fee"
                                    label="Fee (FCFA) *"
                                    type="number"
                                    :rules="[v => v >= 0 || 'Invalid fee']"
                                    prepend-icon="mdi-currency-usd"
                                    required
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12 sm6 class="pa-2">
                                <v-text-field
                                    v-model.number="formData.time_interval_days"
                                    label="Delivery Time (days) *"
                                    type="number"
                                    :rules="[v => v > 0 || 'Invalid delivery time']"
                                    prepend-icon="mdi-clock"
                                    required
                                ></v-text-field>
                            </v-flex>
                        </v-layout>

                        <v-divider class="my-3"></v-divider>

                        <h3 class="primary--text mb-3 body-2">
                            <v-icon color="primary">mdi-gps-fixed</v-icon>
                            GPS Coordinates (optional)
                        </h3>

                        <v-layout row wrap>
                            <v-flex xs12 sm6 class="pa-2">
                                <v-text-field
                                    v-model.number="formData.latitude"
                                    label="Latitude"
                                    type="number"
                                    step="0.000001"
                                    hint="Ex: 3.848000"
                                    persistent-hint
                                    prepend-icon="mdi-crosshairs-gps"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12 sm6 class="pa-2">
                                <v-text-field
                                    v-model.number="formData.longitude"
                                    label="Longitude"
                                    type="number"
                                    step="0.000001"
                                    hint="Ex: 11.517400"
                                    persistent-hint
                                    prepend-icon="mdi-crosshairs-gps"
                                ></v-text-field>
                            </v-flex>
                        </v-layout>

                        <v-alert type="info" :value="true" class="mt-2 mb-3">
                            <v-icon left>mdi-lightbulb-outline</v-icon>
                            <strong>Tip:</strong> Coordinates help with accurate delivery location tracking
                        </v-alert>
                    </v-form>
                </v-card-text>

                <v-card-actions class="pa-3">
                    <v-btn color="grey" flat @click="closeDialog">
                        <v-icon left>mdi-close</v-icon>
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        @click="savePoint"
                        :loading="saving"
                        :disabled="!valid"
                    >
                        <v-icon left>mdi-content-save</v-icon>
                        Save
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Location Info Dialog -->
        <v-dialog v-model="locationInfoDialog" max-width="500px">
            <v-card>
                <v-card-title class="teal darken-2 white--text pa-3">
                    <v-icon left color="white">mdi-information</v-icon>
                    <span class="headline">Location Information</span>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="locationInfoDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="pa-4" v-if="selectedPoint">
                    <div class="location-details">
                        <h3 class="primary--text mb-3">{{ selectedPoint.name }}</h3>

                        <v-layout row wrap class="mb-3">
                            <v-flex xs12>
                                <div class="info-box pa-3 mb-2">
                                    <v-icon color="teal darken-2" class="mr-2">mdi-city</v-icon>
                                    <strong>Town:</strong> {{ selectedPoint.town_name }}
                                </div>
                            </v-flex>
                            <v-flex xs12>
                                <div class="info-box pa-3 mb-2">
                                    <v-icon color="green" class="mr-2">mdi-currency-usd</v-icon>
                                    <strong>Delivery Fee:</strong> {{ selectedPoint.fee }} FCFA
                                </div>
                            </v-flex>
                            <v-flex xs12>
                                <div class="info-box pa-3">
                                    <v-icon color="success" class="mr-2">mdi-gps-fixed</v-icon>
                                    <strong>GPS Coordinates:</strong><br>
                                    <span class="coordinates-display">
                                        Latitude: {{ selectedPoint.latitude }}<br>
                                        Longitude: {{ selectedPoint.longitude }}
                                    </span>
                                </div>
                            </v-flex>
                        </v-layout>

                        <v-alert type="info" :value="true" class="mt-3">
                            <v-icon left>mdi-information</v-icon>
                            This location has GPS coordinates configured for accurate delivery tracking.
                        </v-alert>
                    </div>
                </v-card-text>
                <v-card-actions class="pa-3">
                    <v-spacer></v-spacer>
                    <v-btn color="grey" flat @click="locationInfoDialog = false">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from 'axios'

export default {
    data() {
        return {
            shippingPoints: [],
            towns: [],
            dialog: false,
            autoFillDialog: false,
            locationInfoDialog: false,
            autoFilling: false,
            valid: false,
            saving: false,
            isEditing: false,
            selectedPoint: null,
            filters: {
                town: null,
                hasCoords: null,
                search: ''
            },
            coordsOptions: [
                { text: 'With GPS', value: 'with' },
                { text: 'No GPS', value: 'without' }
            ],
            formData: {
                id: null,
                name: '',
                town: null,
                fee: 1000,
                time_interval_days: 7,
                latitude: null,
                longitude: null
            }
        }
    },

    computed: {
        pointsWithCoords() {
            return this.shippingPoints.filter(p => p.has_coordinates).length
        },
        pointsWithoutCoords() {
            return this.shippingPoints.filter(p => !p.has_coordinates).length
        },
        shippingPointsWithCoords() {
            return this.shippingPoints.filter(p => p.has_coordinates)
        },
        shippingPointsWithoutCoords() {
            return this.shippingPoints.filter(p => !p.has_coordinates)
        },
        townOptions() {
            return this.towns.map(t => ({ text: t.name, value: t.id }))
        },
        filteredPoints() {
            let points = this.shippingPoints

            if (this.filters.town) {
                points = points.filter(p => p.town === this.filters.town)
            }

            if (this.filters.hasCoords === 'with') {
                points = points.filter(p => p.has_coordinates)
            } else if (this.filters.hasCoords === 'without') {
                points = points.filter(p => !p.has_coordinates)
            }

            if (this.filters.search) {
                const search = this.filters.search.toLowerCase()
                points = points.filter(p =>
                    p.name.toLowerCase().includes(search) ||
                    p.town_name.toLowerCase().includes(search)
                )
            }

            return points
        }
    },

    mounted() {
        document.title = "Shipping Points Management"
        this.loadData()
    },

    methods: {
        async loadData() {
            this.$store.commit('setIsLoading', true)
            try {
                const timestamp = new Date().getTime()
                const [pointsRes, townsRes] = await Promise.all([
                    axios.get(`/api/v1/market_place/points/?_=${timestamp}`),
                    axios.get(`/api/v1/market_place/towns/?_=${timestamp}`)
                ])

                this.shippingPoints = pointsRes.data
                this.towns = townsRes.data

                console.log('📊 Loaded points:', this.shippingPoints)

                this.$forceUpdate()

            } catch (error) {
                console.error('Error loading data:', error)
                this.$store.commit('setSnackBarMessage', 'Error loading data')
                this.$store.commit('setSnackBarColor', 'error')
                this.$store.commit('activateSnackBar', true)
            }
            this.$store.commit('setIsLoading', false)
        },

        openAddDialog() {
            this.isEditing = false
            this.formData = {
                id: null,
                name: '',
                town: null,
                fee: 1000,
                time_interval_days: 7,
                latitude: null,
                longitude: null
            }
            this.dialog = true
        },

        editPoint(point) {
            this.isEditing = true
            this.formData = {
                id: point.id,
                name: point.name,
                town: point.town,
                fee: point.fee,
                time_interval_days: point.time_interval_days,
                latitude: point.latitude,
                longitude: point.longitude
            }
            this.dialog = true
        },

        addCoordinates(point) {
            this.editPoint(point)
        },

        showLocationInfo(point) {
            this.selectedPoint = point
            this.locationInfoDialog = true
        },

        async savePoint() {
            if (!this.$refs.form.validate()) return

            this.saving = true
            try {
                const url = this.isEditing
                    ? `/api/v1/market_place/points/${this.formData.id}/`
                    : '/api/v1/market_place/points/'

                const method = this.isEditing ? 'put' : 'post'

                const response = await axios[method](url, this.formData)

                console.log('✅ Point saved:', response.data)

                this.$store.commit('setSnackBarMessage', `Point ${this.isEditing ? 'updated' : 'created'} successfully`)
                this.$store.commit('setSnackBarColor', 'success')
                this.$store.commit('activateSnackBar', true)

                this.closeDialog()

                await this.loadData()
            } catch (error) {
                console.error('Error saving:', error)
                this.$store.commit('setSnackBarMessage', 'Error saving point')
                this.$store.commit('setSnackBarColor', 'error')
                this.$store.commit('activateSnackBar', true)
            }
            this.saving = false
        },

        closeDialog() {
            this.dialog = false
        },

        async autoFillCoordinates() {
            this.autoFilling = true
            try {
                const response = await axios.post('/api/v1/market_place/points/auto-fill-coordinates/')

                if (response.data.success) {
                    this.$store.commit('setSnackBarMessage',
                        `✅ ${response.data.updated_count} point(s) updated, ${response.data.skipped_count} skipped`)
                    this.$store.commit('setSnackBarColor', 'success')
                    this.$store.commit('activateSnackBar', true)

                    this.autoFillDialog = false

                    await this.loadData()
                }
            } catch (error) {
                console.error('Error auto-filling:', error)
                let errorMsg = 'Error auto-filling coordinates'
                if (error.response && error.response.status === 403) {
                    errorMsg = 'Permission denied. Admin access required.'
                }
                this.$store.commit('setSnackBarMessage', errorMsg)
                this.$store.commit('setSnackBarColor', 'error')
                this.$store.commit('activateSnackBar', true)
            }
            this.autoFilling = false
        }
    }
}
</script>

<style scoped>
/* Header responsive */
.title-mobile {
    font-size: 1.25rem !important;
}

/* Statistics cards */
.stat-card {
    border-radius: 8px;
    transition: transform 0.2s ease;
}

.stat-card:hover {
    transform: translateY(-2px);
}

/* Point cards */
.point-card {
    border-radius: 8px;
    transition: all 0.3s ease;
    border-left: 4px solid #00897B;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.point-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.point-card.no-coords {
    border-left-color: #FF9800;
}

/* Info section inside cards */
.info-section {
    background: #F5F5F5;
    padding: 8px;
    border-radius: 6px;
    margin-top: 8px;
    font-size: 0.875rem;
}

/* Location overview */
.location-overview {
    max-height: 400px;
    overflow-y: auto;
}

.location-item {
    padding: 8px;
    border-left: 3px solid #e0e0e0;
    background: #fafafa;
    border-radius: 4px;
}

.coordinates {
    font-family: monospace;
    background: #f5f5f5;
    padding: 2px 6px;
    border-radius: 3px;
    margin-top: 2px;
}

.coordinates-display {
    font-family: monospace;
    background: #e8f5e8;
    padding: 8px;
    border-radius: 4px;
    display: inline-block;
    margin-top: 4px;
}

.location-details .info-box {
    background: white;
    border-radius: 6px;
    border-left: 4px solid #00897B;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
}

/* Mobile optimizations */
@media (max-width: 599px) {
    .title-mobile {
        font-size: 1.1rem !important;
    }

    .point-card {
        margin-bottom: 8px;
    }

    .info-section {
        font-size: 0.8rem;
        padding: 6px;
    }

    .info-section .mb-1 {
        margin-bottom: 4px !important;
    }

    .info-section .mb-2 {
        margin-bottom: 6px !important;
    }

    .point-card .v-card__actions {
        padding: 8px !important;
    }

    .v-chip--small {
        height: 20px !important;
        font-size: 0.7rem !important;
    }

    .v-dialog {
        margin: 8px !important;
    }

    .location-overview {
        max-height: 300px;
    }
}

/* Tablet adjustments */
@media (min-width: 600px) and (max-width: 959px) {
    .stat-card .headline {
        font-size: 1.75rem !important;
    }

    .info-section {
        font-size: 0.85rem;
    }
}

/* Desktop enhancements */
@media (min-width: 960px) {
    .point-card {
        margin-bottom: 0;
    }
}

/* Print styles */
@media print {
    .v-btn,
    .v-chip {
        display: none !important;
    }

    .point-card {
        page-break-inside: avoid;
    }
}

/* Custom scrollbar */
.v-dialog .v-card__text {
    scrollbar-width: thin;
    scrollbar-color: #00897B #f1f1f1;
}

.v-dialog .v-card__text::-webkit-scrollbar {
    width: 8px;
}

.v-dialog .v-card__text::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.v-dialog .v-card__text::-webkit-scrollbar-thumb {
    background: #00897B;
    border-radius: 4px;
}

.v-dialog .v-card__text::-webkit-scrollbar-thumb:hover {
    background: #00695C;
}

/* Animations */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.point-card {
    animation: fadeIn 0.3s ease-in-out;
}

/* Loading state */
.v-card.loading {
    opacity: 0.6;
    pointer-events: none;
}

/* Empty state */
.v-alert {
    border-radius: 8px !important;
}

/* Form improvements */
.v-text-field input,
.v-select input {
    font-size: 0.95rem !important;
}

.v-input--is-focused .v-icon {
    color: #00897B !important;
}

/* Badge for GPS status */
.v-chip.success {
    box-shadow: 0 2px 4px rgba(76, 175, 80, 0.3);
}

.v-chip.warning {
    box-shadow: 0 2px 4px rgba(255, 152, 0, 0.3);
}

/* Hover effects for interactive elements */
.v-btn:not(.v-btn--disabled):hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.v-btn:not(.v-btn--disabled):active {
    transform: translateY(0);
}

/* Tooltip customization */
.v-tooltip__content {
    background: rgba(0, 0, 0, 0.9) !important;
    font-size: 0.875rem !important;
    padding: 8px 12px !important;
    border-radius: 4px !important;
}

/* Success/Error states */
.success--text strong {
    color: #2E7D32 !important;
}

.warning--text strong {
    color: #F57C00 !important;
}

/* Card elevation on focus */
.point-card:focus-within {
    box-shadow: 0 6px 16px rgba(0, 137, 123, 0.2);
    border-left-color: #004D40;
}

/* Accessibility improvements */
.v-btn:focus,
.v-text-field:focus-within,
.v-select:focus-within {
    outline: 2px solid #00897B;
    outline-offset: 2px;
}

/* Better contrast for text */
.grey--text {
    color: #616161 !important;
}

/* Smooth transitions */
* {
    transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}
</style>
